<template>
  <fts-client-modal
    width="1080px"
    :title="fileName"
    append-content-class="document-modal__body"
    append-container-class="document-viewer-container"
    class="fts-document-modal"
    @closed="reset"
    ref="ftsClientModal"
  >
    <!-- header:icon -->
    <font-awesome-icon slot="icon" :icon="['fas', 'print']" fixed-width />
    <!-- body -->
    <template #body>
      <!-- loading indicator -->
      <div class="loading-indicator" v-if="loading">
        <fts-spinner show />
        <div class="loading-indicator-label">Getting document...</div>
      </div>
      <template v-else>
        <!-- pdf -->
        <vue-pdf-app
          theme="light"
          :config="config"
          :file-name="fileName"
          :pdf="previewDocument"
          @open="openHandler"
          @after-created="afterCreated"
        >
          <template #toolbar-right-prepend>
            <button
              type="button"
              class="toolbarButton vue-pdf-app-icon"
              :disabled="loading"
              @click="loadDocument"
            >
              <font-awesome-icon :icon="['fas', 'sync-alt']" size="lg" />
            </button>
          </template>
        </vue-pdf-app>
        <!-- error -->
        <div class="document-error" v-if="error">
          <div class="document-error-label">Error</div>
          <div class="document-error-message">{{error}}</div>
        </div>
      </template>
    </template>
  </fts-client-modal>
</template>

<script>
import VuePdfApp from "vue-pdf-app";
import "vue-pdf-app/dist/icons/main.css";

import { authorizationStore } from '../store/authorizationStore';
import { classificationStore } from "../store/classificationStore";
import { clientStore } from "../store/clientStore";
import webAppConfig from "../commons/webApp.config";

import FTSClientModal from "./FTSClientModal";
import FTSBtn from "./FTSBtn";
import FTSSpinner from "./FTSSpinner";

export default {
  data: () => ({
    pdfApplication: null,
    template: null,
    configTest: {
      sidebar: false,
      toolbar: false,
      secondaryToolbar: false
    },
    config: {
      sidebar: false,
      toolbar: {
        toolbarViewerLeft: {
          findbar: false,
          previous: false,
          next: false
        },
        toolbarViewerRight: {
          presentationMode: false,
          openFile: false,
          viewBookmark: false
        }
      },
      secondaryToolbar: false
    },
    fileName: "preparing...",
    previewDocument: null,
    error: null,
    loading: false
  }),
  components: {
    VuePdfApp,
    "fts-client-modal": FTSClientModal,
    "fts-btn": FTSBtn,
    "fts-spinner": FTSSpinner
  },
  methods: {
    async show(template) {
      this.template = template;
      this.$refs.ftsClientModal.show();
      await this.loadDocument();
    },
    reset() {
      Object.assign(this.$data, this.$options.data.apply(this));
    },
    async loadDocument() {
      let template = this.template;
      this.previewDocument = null;
      this.error = null;
      this.loading = true;
      /* base 64 to array buffer */
      function _base64ToArrayBuffer(base64) {
        var binary_string = window.atob(base64);
        var len = binary_string.length;
        var bytes = new Uint8Array(len);
        for (var i = 0; i < len; i++) {
          bytes[i] = binary_string.charCodeAt(i);
        }
        return bytes.buffer;
      }
      /* request */
      const request = (method, url) => {
        let xhr = new XMLHttpRequest();
        return new Promise(resolve => {
          xhr.open(method, url, true);
          const token = JSON.parse(localStorage.getItem("ls.authorizationData"))["access_token"];
          xhr.setRequestHeader("Content-type", "application/json;charset=UTF-8");
          xhr.setRequestHeader("authorization", "bearer " + token);
          xhr.setRequestHeader("wsId", authorizationStore.getters.runAsWsId || clientStore.getters.workspaceId);
          xhr.setRequestHeader("tz", Intl.DateTimeFormat().resolvedOptions().timeZone)
          xhr.responseType = "arraybuffer";
          xhr.onload = () => resolve(xhr);
          xhr.onerror = () => resolve({
            status: xhr.status,
            statusText: xhr.statusText,
            response: xhr.response,
            responseText: xhr.responseText
          });
          xhr.send();
        });
      }
      /* error handling */
      const errorHandling = (err = {}) => {
        if (err.response && err.response.data && err.response.data.message) {
          this.error = err.response.data.message;
        } else if (err.statusText) {
          this.error = err.statusText;
        } else {
          this.error = 'Something went wrong. Please try again.'
        }
        this.fileName = 'Template';
      }

      if (
        template.url
        && template.parameters
        && !template.parameters.template
      ) {
        try {
          let req = await request('GET', webAppConfig.baseURL ? webAppConfig.baseURL + template.url : template.url);
          if (req && req.status === 200) {
            let filename = "Template";
            let disposition = req.getResponseHeader("Content-Disposition");
            if (disposition) {
              const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
              const matches = filenameRegex.exec(disposition);
              if (matches != null && matches[1]) {
                filename = matches[1].replace(/['"]/g, "");
              }
              this.fileName = decodeURIComponent(filename).replaceAll(/\n/g, "");
            } else {
              this.fileName = "Template";
            }

            let type = req.getResponseHeader("Content-Type");
            let blob;
            if (typeof File === "function") {
              try {
                blob = new File([req.response], filename, { type: type });
              } catch (err) {
                console.error(err);
              }
            }
            if (typeof blob === "undefined") {
              blob = new Blob([req.response], { type: type });
            }
            if (typeof window.navigator.msSaveBlob !== "undefined") {
              // IE workaround for "HTML7007: One or more blob URLs were revoked by closing the blob for which they were created. These URLs will no longer resolve as the data backing the URL has been freed."
              window.navigator.msSaveBlob(blob, filename);
            } else {
              await blob.arrayBuffer().then(buffer => {
                this.previewDocument = buffer;
              });
            }
          } else {
            errorHandling({
              status: req.status,
              statusText: req.statusText
            });
          }
        } catch(err) {
          console.error("error", err);
          errorHandling(err);
        }
      } else {
        try {
          const res = await classificationStore.dispatch("loadDocument", template.parameters);
          this.previewDocument = _base64ToArrayBuffer(res.data.bytes);
          this.fileName = decodeURIComponent(res.data.filename).replaceAll(/\n/g, "");
        } catch (err) {
          console.error("error", err);
          errorHandling(err);
        }
      }
      this.loading = false;
    },
    openHandler(pdfApplication) {
      try {
        pdfApplication.preferences.prefs.printResolution = 300;
        pdfApplication._readPreferences();
      } catch (err) { console.warn("unable set print resolution", err); }
      try {
        pdfApplication.pdfViewer.container.classList.add("fts-scrollbar");
      } catch (err) { console.warn("unable append scrollbar", err); }
      this.pdfApplication = pdfApplication;
    },
    afterCreated(pdfApplication) {
      /* emitted only once when Pdfjs library is binded to vue component. Can be used to set Pdfjs config before pdf document opening. */
    }
  }
};
</script>

<style lang="scss">
@import "./src/assets/scss/vars.scss";

.fts-document-modal {
  .document-viewer-container {
    height: 100%;
    .document-modal__body {
      position: relative;
      padding: 0;
    }
  }
  .pdf-app .pdfViewer .page {
    box-sizing: content-box;
    border-image: none;
  }
  .loading-indicator {
    @include absolute-center-middle;
    z-index: 999;
    .loading-indicator-label {
      margin-top: 1rem;
    }
  }
  .document-error {
    @include absolute-center-middle;
    text-align: center;
    padding: 0.75rem 1rem;
    border-radius: 0.25rem;
    box-shadow: var(--fts-elevation-1);
    background-color: var(--fts-card-background);
    .document-error-label {
      margin-bottom: 0.125rem;
      font-weight: bold;
      font-size: 1rem;
      line-height: 1.5rem;
      color: var(--fts-danger);
    }
    .document-error-message {
      font-size: 0.875rem;
      line-height: 1.25rem;
      color: var(--fts-text);
    }
  }
}
</style>